/**
 * Aliquot (quarter-call) geometry for PLSS sections.
 *
 * LIBRARY-PROMOTION CANDIDATE: nothing here is specific to broker deals — any
 * app that draws PLSS sections and has landman quarter calls ("E2NE",
 * "NW/4 SE/4") could use it. It lives here until the Component Agent moves it
 * into wig-react-library (e.g. next to the mapbox helpers); do not copy it
 * into another app in the meantime.
 *
 * Pure functions, no React, no network.
 *
 * READING A CALL
 * A call is read in landman order, smallest division first: "E2SENE" is the
 * E/2 of the SE/4 of the NE/4. `parseAliquot` returns the parts in that same
 * reading order (["E2", "SE", "NE"]); `aliquotPolygon` applies them from the
 * LAST (largest) to the first (smallest).
 *
 * GEOMETRY — AN APPROXIMATION
 * A section is treated as a bilinear quad spanned by its four corners (the
 * ring vertices nearest the bbox NW/NE/SE/SW corners). An aliquot is a
 * sub-rectangle of the unit square in that quad's (u, v) space, mapped back
 * through the bilinear transform. Lines of constant u or v stay straight
 * under a bilinear map, so four corners describe the piece exactly WITHIN
 * the model. The model itself is the approximation: irregular sections
 * (correction lines, short/long sections along the north and west
 * boundaries, lotted sections, river meanders) are not four-cornered, and
 * the aliquot drawn there is only indicative. Government lots are not
 * modelled at all — `parseAliquot` returns null for them.
 */

import { canonicalTrsKey } from "@warwick-investment-group/wig-react-library";

const QUARTERS = new Set(["NE", "NW", "SE", "SW"]);
const HALVES = new Set(["N2", "S2", "E2", "W2"]);

/** "ALL" / "640" / blank mean the whole section: one call with no parts. */
const WHOLE_SECTION = () => [[]];

// Separators between calls: comma, semicolon, ampersand, the word AND.
const CALL_SPLIT_RE = /\s*(?:,|;|&|\bAND\b)\s*/;

const normalizeCall = (raw) =>
  raw
    .toUpperCase()
    .replace(/½/g, "2")
    // "N/2" -> "N2", "NE/4" -> "NE", "NE4" -> "NE"
    .replace(/([NSEW])\s*\/\s*2/g, "$12")
    .replace(/(NE|NW|SE|SW)\s*\/?\s*4/g, "$1")
    // "E2 OF NE" -> "E2NE"
    .replace(/\bOF\b/g, "")
    .replace(/[\s.]+/g, "");

/**
 * Parse a quartering string into calls.
 *
 * @param {string|null|undefined} str  e.g. "E2NE, E2SENE", "N/2", "NE/4 SW/4"
 * @returns {string[][]|null}
 *   An array of calls, each an array of parts in landman (reading) order.
 *   `[[]]` means the whole section (blank, "ALL", "640"). `null` means the
 *   string could not be read — lots, metes-and-bounds, junk — and the caller
 *   should fall back to the whole section.
 */
export const parseAliquot = (str) => {
  if (str === null || str === undefined) return WHOLE_SECTION();
  if (typeof str !== "string") return null;
  const trimmed = str.trim().toUpperCase();
  if (trimmed === "" || trimmed === "ALL" || trimmed === "640") {
    return WHOLE_SECTION();
  }
  // Government lots can't be placed from the quarter-call grid.
  if (/\bLOTS?\b|\bL\d/.test(trimmed)) return null;

  const segments = trimmed.split(CALL_SPLIT_RE).filter((s) => s.trim() !== "");
  if (segments.length === 0) return WHOLE_SECTION();

  const calls = [];
  for (const seg of segments) {
    const s = seg.trim();
    if (s === "ALL" || s === "640") {
      calls.push([]);
      continue;
    }
    const norm = normalizeCall(s);
    if (norm.length === 0 || norm.length % 2 !== 0) return null;
    const parts = [];
    for (let i = 0; i < norm.length; i += 2) {
      const tok = norm.slice(i, i + 2);
      if (!QUARTERS.has(tok) && !HALVES.has(tok)) return null;
      parts.push(tok);
    }
    calls.push(parts);
  }
  return calls;
};

/**
 * Narrow the unit square by the parts, largest (last) first.
 * u runs west (0) -> east (1); v runs south (0) -> north (1).
 *
 * @param {string[]} parts  reading order, e.g. ["E2", "SE", "NE"]
 * @returns {{u0:number,u1:number,v0:number,v1:number}}
 */
export const aliquotRect = (parts) => {
  let u0 = 0;
  let u1 = 1;
  let v0 = 0;
  let v1 = 1;
  const list = Array.isArray(parts) ? parts : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i];
    const um = (u0 + u1) / 2;
    const vm = (v0 + v1) / 2;
    const north = p === "N2" || p === "NE" || p === "NW";
    const south = p === "S2" || p === "SE" || p === "SW";
    const east = p === "E2" || p === "NE" || p === "SE";
    const west = p === "W2" || p === "NW" || p === "SW";
    if (north) v0 = vm;
    if (south) v1 = vm;
    if (east) u0 = um;
    if (west) u1 = um;
  }
  return { u0, u1, v0, v1 };
};

// The outer ring from Polygon coordinates (rings[]) or a bare ring.
const outerRing = (coords) => {
  if (!Array.isArray(coords) || coords.length === 0) return null;
  // Bare ring: first element is a position [x, y].
  if (typeof coords[0]?.[0] === "number") return coords;
  // Polygon: first element is the outer ring.
  if (Array.isArray(coords[0]) && typeof coords[0][0]?.[0] === "number") {
    return coords[0];
  }
  return null;
};

/**
 * The four corners of a section ring: for each bbox corner, the ring vertex
 * nearest to it (longitude scaled by cos(latitude) so distances are roughly
 * isotropic).
 *
 * @returns {{sw:number[],se:number[],ne:number[],nw:number[]}|null}
 */
export const sectionCorners = (coords) => {
  const ring = outerRing(coords);
  if (!ring || ring.length < 4) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!(maxX > minX) || !(maxY > minY)) return null;
  const kx = Math.cos((((minY + maxY) / 2) * Math.PI) / 180);
  const nearest = (tx, ty) => {
    let best = null;
    let bestD = Infinity;
    for (const pt of ring) {
      const dx = (pt[0] - tx) * kx;
      const dy = pt[1] - ty;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = pt;
      }
    }
    return [best[0], best[1]];
  };
  return {
    sw: nearest(minX, minY),
    se: nearest(maxX, minY),
    ne: nearest(maxX, maxY),
    nw: nearest(minX, maxY),
  };
};

const bilinear = ({ sw, se, ne, nw }, u, v) => {
  const a = (1 - u) * (1 - v);
  const b = u * (1 - v);
  const c = u * v;
  const d = (1 - u) * v;
  return [
    a * sw[0] + b * se[0] + c * ne[0] + d * nw[0],
    a * sw[1] + b * se[1] + c * ne[1] + d * nw[1],
  ];
};

/**
 * Polygon ring for an aliquot of a section.
 *
 * @param {Array} sectionPolygonCoords  Polygon coordinates (array of rings)
 *   or a bare outer ring, WGS84 [lng, lat].
 * @param {string[]} parts  one call from `parseAliquot`, reading order
 * @returns {number[][]|null} closed ring SW -> SE -> NE -> NW -> SW
 *   (counter-clockwise), or null when the section has no usable corners.
 */
export const aliquotPolygon = (sectionPolygonCoords, parts) => {
  const corners = sectionCorners(sectionPolygonCoords);
  if (!corners) return null;
  const distinct = new Set(
    [corners.sw, corners.se, corners.ne, corners.nw].map((p) => p.join(","))
  );
  if (distinct.size < 4) return null;
  const { u0, u1, v0, v1 } = aliquotRect(parts);
  const sw = bilinear(corners, u0, v0);
  return [
    sw,
    bilinear(corners, u1, v0),
    bilinear(corners, u1, v1),
    bilinear(corners, u0, v1),
    [sw[0], sw[1]],
  ];
};

/**
 * Stable key for the land a quartering describes: the same calls in any
 * order or notation give the same key ("NE, E2NW" = "e/2 nw/4 & ne").
 * "" is the whole section; null means the quartering can't be read.
 */
export const aliquotKey = (str) => {
  const calls = parseAliquot(str);
  if (calls === null) return null;
  return calls
    .map((c) => c.join(""))
    .sort()
    .join(",");
};

// Sections along a township's north and west edges absorb the survey's
// closing errors and carry government lots: 1-7 (north row + NW corner),
// 18, 19, 30, 31 (west column).
const LOTTED_SECTIONS = new Set([1, 2, 3, 4, 5, 6, 7, 18, 19, 30, 31]);

/**
 * True for a section whose aliquots this module can only approximate
 * (irregular, lotted). @param {string|number} sec
 */
export const isLottedSection = (sec) =>
  LOTTED_SECTIONS.has(parseInt(String(sec ?? "").trim(), 10));

// ---------------------------------------------------------------------------
// TRS join
// ---------------------------------------------------------------------------

/**
 * Padding-free TRS key so the survey grid ("007N-007W-21") and the tract
 * columns ("07N", "07W", "21") meet: "7N-7W-21". Returns null when any part
 * is missing or not a township / range / section. The join key itself is
 * wig-react-library's `canonicalTrsKey` (shared with the report map); this
 * name stays so the map code reads the same.
 */
export const canonicalTrs = (twp, rng, sec) => canonicalTrsKey(twp, rng, sec);

/**
 * Index a sections FeatureCollection (props twp / rng / sec, as served by
 * /api/spatial/plss/sections) by `canonicalTrs`.
 *
 * @returns {Map<string, object>}
 */
export const indexSectionsByTrs = (fc) => {
  const out = new Map();
  for (const f of Array.isArray(fc?.features) ? fc.features : []) {
    const p = f?.properties || {};
    const key = canonicalTrs(p.twp, p.rng, p.sec);
    if (key && !out.has(key)) out.set(key, f);
  }
  return out;
};

// Largest polygon of a Polygon / MultiPolygon, as Polygon coordinates.
const mainPolygon = (geom) => {
  if (!geom) return null;
  if (geom.type === "Polygon") return geom.coordinates;
  if (geom.type === "MultiPolygon") {
    let best = null;
    let bestArea = -1;
    for (const poly of geom.coordinates || []) {
      const ring = poly?.[0] || [];
      let area = 0;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        area += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
      }
      area = Math.abs(area);
      if (area > bestArea) {
        bestArea = area;
        best = poly;
      }
    }
    return best;
  }
  return null;
};

// Mapbox feature properties must be flat; drop nested objects/arrays.
const flatProps = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === null || ["string", "number", "boolean"].includes(typeof v)) {
      out[k] = v;
    }
  }
  return out;
};

/**
 * One GeoJSON feature per tract — a MultiPolygon with one piece per call —
 * carrying the tract's own (flat) fields for popups. Tracts whose section is
 * not in the index are skipped. A quartering that can't be read falls back
 * to the whole section and is marked `aliquot_fallback: true`.
 *
 * @param {Array<object>} tracts  rows with township / range / section /
 *   quartering, plus whatever the caller joined on (e.g. deal_key)
 * @param {Map<string, object>} sectionFeaturesByCanonicalTrs  from
 *   `indexSectionsByTrs`
 * @returns {Array<object>} GeoJSON Features
 */
export const tractFeatures = (tracts, sectionFeaturesByCanonicalTrs) => {
  const index = sectionFeaturesByCanonicalTrs;
  if (!index || typeof index.get !== "function") return [];
  const out = [];
  for (const t of Array.isArray(tracts) ? tracts : []) {
    const key = canonicalTrs(t?.township, t?.range, t?.section);
    if (!key) continue;
    const section = index.get(key);
    const poly = mainPolygon(section?.geometry);
    if (!poly) continue;
    const parsed = parseAliquot(t?.quartering);
    const calls = parsed ?? WHOLE_SECTION();
    const pieces = [];
    for (const parts of calls) {
      const ring = aliquotPolygon(poly, parts);
      if (ring) pieces.push([ring]);
    }
    if (pieces.length === 0) continue;
    out.push({
      type: "Feature",
      geometry: { type: "MultiPolygon", coordinates: pieces },
      properties: {
        ...flatProps(t),
        trs_canonical: key,
        aliquot_fallback: parsed === null,
      },
    });
  }
  return out;
};

/**
 * Key for the INTEREST a tract row describes, for de-duplicating acres: one
 * owner (lessor/grantor) in one aliquot of one section. Many owners hold
 * undivided shares of the same aliquot, and their acres add up; only
 * competing deals for the SAME owner's interest there share a key (and count
 * once). A row whose quartering can't be read, or that has no TRS or no
 * lessor, can't be compared, so it keys on its own id and stays separate.
 *
 * @param {{ id?: any, township?: any, range?: any, section?: any,
 *   quartering?: string, lessor_grantor?: string }} row
 * @returns {string}
 */
export const tractLandKey = (row) => {
  const trs = canonicalTrs(row?.township, row?.range, row?.section);
  const ak = trs ? aliquotKey(row?.quartering) : null;
  const owner = String(row?.lessor_grantor ?? "").trim().toUpperCase();
  if (trs && ak !== null && owner) return `${trs}|${ak}|${owner}`;
  return `tract:${row?.id ?? Math.random()}`;
};
